// src/lib/repositories/supabaseBibleRepository.js
// Supabase implementation of BibleRepository interface

import { getSupabaseClient } from '../supabase/client';
import { createAdminClient } from '../supabase/admin';

/**
 * Supabase implementation of BibleRepository
 * Handles all database operations with proper snake_case to camelCase conversion
 */
class SupabaseBibleRepository {
  // Get the appropriate Supabase client (server or browser)
  async _getClient() {
    // If running on server (API routes, server components)
    if (typeof window === 'undefined') {
      try {
        // CRITICAL FIX: Use absolute path from project root instead of relative path
        // This ensures webpack can properly resolve the module during build
        const { createClient: createServerClient } = await import('@/lib/supabase/server');
        const serverClient = await createServerClient();
        return serverClient;
      } catch (error) {
        console.error('[Repository] Failed to create server client:', error?.message);
        // Fallback to browser client if server client fails
        if (typeof window !== 'undefined') {
          return getSupabaseClient();
        }
        return null;
      }
    }
    
    // If running on browser (client components)
    return getSupabaseClient();
  }

  // Get admin client for operations that need to bypass RLS
  _getAdminClient() {
    return createAdminClient();
  }

  // =====================================================
  // TRANSLATION OPERATIONS
  // =====================================================

  async listTranslations() {
    const supabase = await this._getClient();
    
    // Safety check for undefined client
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }
    
    const { data, error } = await supabase?.from('bible_translations')?.select('*')?.order('code');
    
    if (error) throw new Error(`Failed to list translations: ${error.message}`);
    return data?.map(this._mapTranslation) || [];
  }

  // =====================================================
  // BOOK OPERATIONS
  // =====================================================

  async listBooks(translation) {
    const supabase = await this._getClient();
    
    // Add validation for translation parameter
    if (!translation) {
      console.warn('No translation code provided to listBooks');
      return [];
    }
    
    // First, get the translation ID
    const translationData = await this.getTranslationByCode(translation);
    if (!translationData) {
      console.error(`Translation ${translation} not found in database`);
      throw new Error(`Translation ${translation} not found. Please ensure the translation is imported into the database.`);
    }
    
    // Then query books by translation_id - FIXED: Use correct table name from migration
    const { data, error } = await supabase
      ?.from('bible_books')
      ?.select('*')
      ?.eq('translation_id', translationData?.id)
      ?.order('book_number');
    
    if (error) throw new Error(`Failed to list books: ${error.message}`);
    
    // Add translation code to each book for mapping
    const booksWithTranslation = data?.map(book => ({
      ...book,
      bible_translations: { code: translation }
    })) || [];
    
    return booksWithTranslation?.map(this._mapBook) || [];
  }

  async getBookByCode(translation, bookCode) {
    const supabase = await this._getClient();
    
    // First, get the translation ID
    const translationData = await this.getTranslationByCode(translation);
    if (!translationData) {
      throw new Error(`Translation ${translation} not found`);
    }
    
    // Then query the book by translation_id and book_code - FIXED: Use correct table name from migration
    const { data, error } = await supabase
      ?.from('bible_books')
      ?.select('*')
      ?.eq('translation_id', translationData?.id)
      ?.eq('book_code', bookCode)
      ?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null; // Not found
      throw new Error(`Failed to get book: ${error.message}`);
    }
    
    // Add translation code for mapping
    const bookWithTranslation = {
      ...data,
      bible_translations: { code: translation }
    };
    
    return this._mapBook(bookWithTranslation);
  }

  async createOrUpdateBook(book) {
    const supabase = await this._getClient();
    // Convert camelCase to snake_case for DB
    const dbBook = {
      id: book?.id,
      translation_id: book?.translationId,
      book_number: book?.bookNumber,
      book_code: book?.bookCode,
      book_name: book?.bookName,
      testament: book?.testament,
      chapter_count: book?.chapterCount,
      short_name: book?.shortName,
    };

    // FIXED: Use correct table name from migration (bible_books, not bible_admin_books)
    const { data, error } = await supabase?.from('bible_books')?.upsert(dbBook)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert book: ${error.message}`);
    return this._mapBook(data);
  }

  // =====================================================
  // CHAPTER OPERATIONS
  // =====================================================

  async listChapters(bookId) {
    const supabase = await this._getClient();
    // FIXED: Use correct table name from migration (bible_chapters, not bible_admin_chapters)
    const { data, error } = await supabase?.from('bible_chapters')?.select('*')?.eq('book_id', bookId)?.order('chapter_number');
    
    if (error) throw new Error(`Failed to list chapters: ${error.message}`);
    return data?.map(this._mapChapter) || [];
  }

  async getChapter(bookId, chapterNumber) {
    const supabase = await this._getClient();
    // FIXED: Use correct table name from migration (bible_chapters, not bible_admin_chapters)
    const { data, error } = await supabase?.from('bible_chapters')?.select('*')?.eq('book_id', bookId)?.eq('chapter_number', chapterNumber)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get chapter: ${error.message}`);
    }
    return this._mapChapter(data);
  }

  async createOrUpdateChapter(chapter) {
    const supabase = await this._getClient();
    const dbChapter = {
      id: chapter?.id,
      book_id: chapter?.bookId,
      chapter_number: chapter?.chapterNumber,
      title: chapter?.title,
      outline: chapter?.outline,
      introduction: chapter?.introduction,
      verse_count: chapter?.verseCount,
    };

    // FIXED: Use correct table name from migration (bible_chapters, not bible_admin_chapters)
    const { data, error } = await supabase?.from('bible_chapters')?.upsert(dbChapter)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert chapter: ${error.message}`);
    return this._mapChapter(data);
  }

  // =====================================================
  // VERSE OPERATIONS
  // =====================================================

  async listVerses(chapterId) {
    const supabase = await this._getClient();
    // FIXED: Use correct table name from migration (bible_verses, not bible_admin_verses)
    const { data, error } = await supabase?.from('bible_verses')?.select('*')?.eq('chapter_id', chapterId)?.order('verse_number');
    
    if (error) throw new Error(`Failed to list verses: ${error.message}`);
    return data?.map(this._mapVerse) || [];
  }

  async getVerseByNumber(chapterId, verseNumber) {
    const supabase = await this._getClient();
    // FIXED: Use correct table name from migration (bible_verses, not bible_admin_verses)
    const { data, error } = await supabase?.from('bible_verses')?.select('*')?.eq('chapter_id', chapterId)?.eq('verse_number', verseNumber)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get verse: ${error.message}`);
    }
    return this._mapVerse(data);
  }

  async createOrUpdateVerse(verse) {
    const supabase = await this._getClient();
    const dbVerse = {
      id: verse?.id,
      chapter_id: verse?.chapterId,
      verse_number: verse?.verseNumber,
      verse_text: verse?.text,
      translation: verse?.translation || 'KJV',
    };

    // FIXED: Use correct table name from migration (bible_verses, not bible_admin_verses)
    const { data, error } = await supabase?.from('bible_verses')?.upsert(dbVerse)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert verse: ${error.message}`);
    return this._mapVerse(data);
  }

  // =====================================================
  // COMMENTARY OPERATIONS
  // =====================================================

  async getExperiencingChristCommentary(verseId) {
    const supabase = await this._getClient();
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    const { data, error } = await supabase?.from('bible_commentary_experiencing_christ')?.select('*')?.eq('verse_id', verseId)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get commentary: ${error.message}`);
    }
    return this._mapCommentary(data);
  }

  async upsertExperiencingChristCommentary(entry) {
    const supabase = await this._getClient();
    if (!supabase) {
      throw new Error('Supabase client not initialized - check environment variables');
    }

    // Verify required fields
    if (!entry?.verseId) {
      throw new Error('verseId is required for commentary');
    }
    if (!entry?.content) {
      throw new Error('content is required for commentary');
    }

    const dbEntry = {
      id: entry?.id,
      verse_id: entry?.verseId,
      content: entry?.content,
    };

    const { data, error } = await supabase?.from('bible_commentary_experiencing_christ')?.upsert(dbEntry)?.select()?.single();
    
    if (error) {
      // Provide detailed error message
      throw new Error(`Failed to upsert commentary: ${error.message} (Code: ${error.code})`);
    }
    
    return this._mapCommentary(data);
  }

  async getPrayReadEntry(verseId) {
    const supabase = await this._getClient();
    const { data, error } = await supabase?.from('bible_pray_read_entries')?.select('*')?.eq('verse_id', verseId)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get pray-read entry: ${error.message}`);
    }
    return this._mapPrayRead(data);
  }

  async upsertPrayReadEntry(entry) {
    const supabase = await this._getClient();
    const dbEntry = {
      id: entry?.id,
      verse_id: entry?.verseId,
      content: entry?.content,
    };

    const { data, error } = await supabase?.from('bible_pray_read_entries')?.upsert(dbEntry)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert pray-read entry: ${error.message}`);
    return this._mapPrayRead(data);
  }

  // =====================================================
  // WORD COMMENTARY OPERATIONS
  // =====================================================

  async listWordCommentaries(verseId) {
    const supabase = await this._getClient();
    const { data, error } = await supabase?.from('bible_word_commentary')?.select('*')?.eq('verse_id', verseId)?.order('created_at');
    
    if (error) throw new Error(`Failed to list word commentaries: ${error.message}`);
    return data?.map(this._mapWordCommentary) || [];
  }

  async upsertWordCommentary(entry) {
    const supabase = await this._getClient();
    const dbEntry = {
      id: entry?.id,
      verse_id: entry?.verseId,
      word: entry?.word,
      content: entry?.content,
    };

    const { data, error } = await supabase?.from('bible_word_commentary')?.upsert(dbEntry)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert word commentary: ${error.message}`);
    return this._mapWordCommentary(data);
  }

  async deleteWordCommentary(id) {
    const supabase = await this._getClient();
    const { error } = await supabase?.from('bible_word_commentary')?.delete()?.eq('id', id);
    
    if (error) throw new Error(`Failed to delete word commentary: ${error.message}`);
  }

  // =====================================================
  // CROSS REFERENCE OPERATIONS
  // =====================================================

  async listCrossReferences(verseId) {
    const supabase = await this._getClient();
    const { data, error } = await supabase?.from('bible_cross_references')?.select('*')?.eq('verse_id', verseId)?.order('created_at');
    
    if (error) throw new Error(`Failed to list cross references: ${error.message}`);
    return data?.map(this._mapCrossReference) || [];
  }

  async upsertCrossReference(entry) {
    const supabase = await this._getClient();
    const dbEntry = {
      id: entry?.id,
      verse_id: entry?.verseId,
      reference: entry?.reference,
      note: entry?.note,
    };

    const { data, error } = await supabase?.from('bible_cross_references')?.upsert(dbEntry)?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert cross reference: ${error.message}`);
    return this._mapCrossReference(data);
  }

  async deleteCrossReference(id) {
    const supabase = await this._getClient();
    const { error } = await supabase?.from('bible_cross_references')?.delete()?.eq('id', id);
    
    if (error) throw new Error(`Failed to delete cross reference: ${error.message}`);
  }

  // =====================================================
  // BOOK METADATA OPERATIONS (NEW)
  // =====================================================

  async getBookMeta(bookId) {
    const supabase = await this._getClient();
    const { data, error } = await supabase?.from('bible_book_meta')?.select('*')?.eq('book_id', bookId)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get book metadata: ${error.message}`);
    }
    return this._mapBookMeta(data);
  }

  async upsertBookMeta(bookId, meta) {
    const supabase = await this._getClient();
    const dbMeta = {
      book_id: bookId,
      outline: meta?.outline || '',
      introduction: meta?.introduction || '',
    };

    const { data, error } = await supabase?.from('bible_book_meta')?.upsert(dbMeta, { onConflict: 'book_id' })?.select()?.single();
    
    if (error) throw new Error(`Failed to upsert book metadata: ${error.message}`);
    return this._mapBookMeta(data);
  }

  // =====================================================
  // BULK OPERATIONS (ENHANCED)
  // =====================================================

  /**
   * Bulk upsert books using RPC function
   * @param {Array<Object>} books - Array of book objects
   * @returns {Promise<Object>} Result with success status and counts
   */
  async bulkUpsertBooks(books) {
    try {
      console.log('🔐 Using RPC function for bulk book import (RLS bypassed via SECURITY DEFINER)');
      console.log(`📚 Attempting to upsert ${books?.length} books`);

      // Call the RPC function
      const { data, error } = await this.client?.rpc('bulk_upsert_bible_books', {
        books_data: books
      });

      if (error) {
        console.error('❌ RPC function error:', error);
        throw error;
      }

      // Check if the RPC function returned an error
      if (data && !data?.success) {
        console.error('❌ RPC function returned error:', data?.error);
        throw new Error(data.error);
      }

      console.log('✅ Successfully upserted books:', data);
      return {
        success: true,
        inserted: data?.inserted || 0,
        updated: data?.updated || 0,
        total: data?.total || 0
      };
    } catch (error) {
      console.error('❌ Error in bulkUpsertBooks:', error);
      throw error;
    }
  }

  /**
   * Bulk upsert chapters using RPC function
   * @param {Array<Object>} chapters - Array of chapter objects
   * @returns {Promise<Object>} Result with success status and counts
   */
  async bulkUpsertChapters(chapters) {
    try {
      console.log('🔐 Using RPC function for bulk chapter import (RLS bypassed via SECURITY DEFINER)');
      console.log(`📖 Attempting to upsert ${chapters?.length} chapters`);

      // Call the RPC function
      const { data, error } = await this.client?.rpc('bulk_upsert_bible_chapters', {
        chapters_data: chapters
      });

      if (error) {
        console.error('❌ RPC function error:', error);
        throw error;
      }

      // Check if the RPC function returned an error
      if (data && !data?.success) {
        console.error('❌ RPC function returned error:', data?.error);
        throw new Error(data.error);
      }

      console.log('✅ Successfully upserted chapters:', data);
      return {
        success: true,
        inserted: data?.inserted || 0,
        updated: data?.updated || 0,
        total: data?.total || 0
      };
    } catch (error) {
      console.error('❌ Error in bulkUpsertChapters:', error);
      throw error;
    }
  }

  /**
   * Bulk upsert verses using RPC function
   * @param {Array<Object>} verses - Array of verse objects
   * @returns {Promise<Object>} Result with success status and counts
   */
  async bulkUpsertVerses(verses) {
    try {
      console.log('🔐 Using RPC function for bulk verse import (RLS bypassed via SECURITY DEFINER)');
      console.log(`📝 Attempting to upsert ${verses?.length} verses`);

      // Call the RPC function
      const { data, error } = await this.client?.rpc('bulk_upsert_bible_verses', {
        verses_data: verses
      });

      if (error) {
        console.error('❌ RPC function error:', error);
        throw error;
      }

      // Check if the RPC function returned an error
      if (data && !data?.success) {
        console.error('❌ RPC function returned error:', data?.error);
        throw new Error(data.error);
      }

      console.log('✅ Successfully upserted verses:', data);
      return {
        success: true,
        inserted: data?.inserted || 0,
        updated: data?.updated || 0,
        total: data?.total || 0
      };
    } catch (error) {
      console.error('❌ Error in bulkUpsertVerses:', error);
      throw error;
    }
  }

  // =====================================================
  // GENESIS-SPECIFIC OPERATIONS (NEW)
  // =====================================================

  async getGenesisData(translationCode = 'KJV_EN') {
    try {
      // Get Genesis book with metadata
      const book = await this.getBookByCode(translationCode, 'GEN');
      if (!book) return null;

      // Get book metadata
      const meta = await this.getBookMeta(book?.id);

      // Get all chapters with verses
      const chapters = await this.listChapters(book?.id);
      
      const chaptersWithVerses = await Promise.all(
        chapters?.map(async (chapter) => {
          const verses = await this.listVerses(chapter?.id);
          return {
            ...chapter,
            verses,
          };
        })
      );

      return {
        ...book,
        meta,
        chapters: chaptersWithVerses,
      };
    } catch (error) {
      console.error('Failed to get Genesis data:', error);
      throw error;
    }
  }

  // =====================================================
  // EXODUS-SPECIFIC OPERATIONS (NEW)
  // =====================================================

  async getExodusData(translationCode = 'KJV_EN') {
    try {
      // Get Exodus book with metadata
      const book = await this.getBookByCode(translationCode, 'EXO');
      if (!book) return null;

      // Get book metadata
      const meta = await this.getBookMeta(book?.id);

      // Get all chapters with verses
      const chapters = await this.listChapters(book?.id);
      
      const chaptersWithVerses = await Promise.all(
        chapters?.map(async (chapter) => {
          const verses = await this.listVerses(chapter?.id);
          return {
            ...chapter,
            verses,
          };
        })
      );

      return {
        ...book,
        meta,
        chapters: chaptersWithVerses,
      };
    } catch (error) {
      console.error('Failed to get Exodus data:', error);
      throw error;
    }
  }

  async getBookById(bookId) {
    const supabase = await this._getClient();
    // FIXED: Use correct table name from migration (bible_books, not bible_admin_books)
    const { data, error} = await supabase?.from('bible_books')?.select('*, bible_translations(code)')?.eq('id', bookId)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw new Error(`Failed to get book: ${error.message}`);
    }
    return this._mapBook(data);
  }

  async getTranslationByCode(code) {
    const supabase = await this._getClient();
    
    // Safety check for undefined client
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return null;
    }
    
    // Validate code format before querying
    if (!code || typeof code !== 'string') {
      console.warn(`Invalid translation code format: ${code}`);
      return null;
    }
    
    // CRITICAL FIX: Decode URL-encoded characters and validate
    // This handles cases where the URL parameter contains encoded characters like %3A (:)
    let decodedCode = code;
    try {
      decodedCode = decodeURIComponent(code);
    } catch (e) {
      console.warn(`Failed to decode translation code: ${code}`);
    }
    
    // Check if decoded code still contains invalid characters (like ':')
    if (decodedCode?.includes(':')) {
      console.warn(`Invalid translation code contains colon: ${decodedCode}`);
      return null;
    }
    
    // CRITICAL FIX: Normalize translation code to uppercase to match enum values
    // The database enum is defined as ENUM ('KJV_EN', 'LUGANDA') - all uppercase
    const normalizedCode = decodedCode?.toUpperCase();
    
    const { data, error } = await supabase?.from('bible_translations')?.select('*')?.eq('code', normalizedCode)?.single();
    
    if (error) {
      // Handle not found gracefully
      if (error?.code === 'PGRST116') {
        console.warn(`Translation code "${code}" (normalized to "${normalizedCode}") not found in database. Available translations should be queried first.`);
        return null;
      }
      // For other errors (like enum constraint violations), log and return null instead of throwing
      console.error(`Error fetching translation "${code}" (normalized to "${normalizedCode}"):`, error?.message);
      return null;
    }
    return this._mapTranslation(data);
  }

  // =====================================================
  // MAPPING FUNCTIONS (NEW)
  // =====================================================

  _mapTranslation(row) {
    return {
      id: row?.id,
      code: row?.code,
      name: row?.name,
      language: row?.language,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapBook(row) {
    return {
      id: row?.id,
      translation: row?.bible_translations?.code || row?.translation_id,
      translationId: row?.translation_id,
      bookNumber: row?.book_number,
      bookCode: row?.book_code,
      bookName: row?.book_name,
      testament: row?.testament,
      chapterCount: row?.chapter_count,
      shortName: row?.short_name,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapChapter(row) {
    return {
      id: row?.id,
      bookId: row?.book_id,
      chapterNumber: row?.chapter_number,
      title: row?.title,
      outline: row?.outline,
      introduction: row?.introduction,
      verseCount: row?.verse_count,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapVerse(row) {
    return {
      id: row?.id,
      chapterId: row?.chapter_id,
      verseNumber: row?.verse_number,
      text: row?.verse_text,
      translation: row?.translation,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapCommentary(row) {
    return {
      id: row?.id,
      verseId: row?.verse_id,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapPrayRead(row) {
    return {
      id: row?.id,
      verseId: row?.verse_id,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapWordCommentary(row) {
    return {
      id: row?.id,
      verseId: row?.verse_id,
      word: row?.word,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapCrossReference(row) {
    return {
      id: row?.id,
      verseId: row?.verse_id,
      reference: row?.reference,
      note: row?.note,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }

  _mapBookMeta(row) {
    if (!row) return null;
    return {
      id: row?.id,
      bookId: row?.book_id,
      outline: row?.outline,
      introduction: row?.introduction,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    };
  }
}

// Export singleton instance
export const supabaseBibleRepository = new SupabaseBibleRepository();